import { createApp } from 'vue';
import axios from 'axios';

// Configure axios defaults
axios.defaults.headers.common['X-Requested-With'] = 'XMLHttpRequest';

// Get CSRF token from meta tag
const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
if (csrfToken) {
    axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;
} else {
    console.warn('CSRF token not found in meta tag');
}

// Enable credentials for session-based authentication
axios.defaults.withCredentials = true;

// Set base URL for API requests
axios.defaults.baseURL = window.location.origin;

// Add request interceptor for debugging
axios.interceptors.request.use(
    config => {
        console.log('Making request:', config.method?.toUpperCase(), config.url);
        console.log('Headers:', config.headers);
        return config;
    },
    error => {
        console.error('Request error:', error);
        return Promise.reject(error);
    }
);

// Add response interceptor for debugging
axios.interceptors.response.use(
    response => {
        console.log('Response received:', response.status, response.data);
        return response;
    },
    error => {
        console.error('Response error:', error.response?.status, error.response?.data);
        return Promise.reject(error);
    }
);

// Global Vue configuration
const VueConfig = {
    // Global properties available in all components
    globalProperties: {
        $http: axios,
        $route: (name, params = {}) => {
            // Helper function to generate Laravel routes (you can enhance this)
            return window.route ? window.route(name, params) : '#';
        }
    }
};

// Vue component registry
window.VueComponents = {};

// Function to register Vue components globally
window.registerVueComponent = function(name, component) {
    window.VueComponents[name] = component;
    console.log(`Vue component '${name}' registered`);
};

// Function to create and mount Vue component
window.mountVueComponent = function(componentName, elementSelector, props = {}) {
    if (!window.VueComponents[componentName]) {
        console.error(`Vue component '${componentName}' not found. Available components:`, Object.keys(window.VueComponents));
        return null;
    }

    const element = document.querySelector(elementSelector);
    if (!element) {
        console.error(`Element '${elementSelector}' not found`);
        return null;
    }

    try {
        // Create Vue app instance for this component
        const app = createApp(window.VueComponents[componentName], props);

        // Apply global configuration
        Object.assign(app.config.globalProperties, VueConfig.globalProperties);

        // Mount the component
        const instance = app.mount(element);

        console.log(`Vue component '${componentName}' mounted on '${elementSelector}'`);
        return { app, instance };
    } catch (error) {
        console.error(`Error mounting Vue component '${componentName}':`, error);
        return null;
    }
};

// Function to auto-initialize Vue components
window.initializeVueComponents = function() {
    console.log('Initializing Vue components...');

    // Find all elements with data-vue-component attribute
    const vueElements = document.querySelectorAll('[data-vue-component]');

    if (vueElements.length === 0) {
        console.log('No Vue components found on this page');
        return;
    }

    vueElements.forEach((element, index) => {
        const componentName = element.getAttribute('data-vue-component');
        const propsData = element.getAttribute('data-vue-props');
        let props = {};

        // Parse props if provided
        if (propsData) {
            try {
                props = JSON.parse(propsData);
            } catch (e) {
                console.error(`Invalid JSON in data-vue-props for component '${componentName}':`, propsData);
            }
        }

        // Generate unique ID if not exists
        if (!element.id) {
            element.id = `vue-component-${componentName}-${index}`;
        }

        // Mount the component
        window.mountVueComponent(componentName, '#' + element.id, props);
    });
};

// Function to manually mount a component (useful for dynamic content)
window.mountVueComponentManually = function(componentName, elementId, props = {}) {
    return window.mountVueComponent(componentName, '#' + elementId, props);
};

// Export for use in other modules
const mountVueComponent = window.mountVueComponent,
    registerVueComponent = window.registerVueComponent,
    initializeVueComponents = window.initializeVueComponents
export {
    VueConfig,
    mountVueComponent,
    registerVueComponent,
    initializeVueComponents
};
